import { LucideIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StepHeader } from "./StepHeader";
import { parseHeight, parseAge } from "@/utils/heightParser";
import { useState, useEffect } from "react";
interface StatsStepProps {
  icon: LucideIcon;
  title: string;
  description: string;
  age: string;
  setAge: (value: string) => void;
  sex: string;
  setSex: (value: string) => void;
  height: string;
  setHeight: (value: string) => void;
  weight: string;
  setWeight: (value: string) => void;
  experience: string;
  setExperience: (value: string) => void;
  heightError?: string;
  setHeightError?: (error: string) => void;
  ageError?: string;
  setAgeError?: (error: string) => void;
}

export function StatsStep({
  icon,
  title,
  description,
  age,
  setAge,
  sex,
  setSex,
  height,
  setHeight,
  weight,
  setWeight,
  experience,
  setExperience,
  heightError,
  setHeightError,
  ageError,
  setAgeError,
}: StatsStepProps) {
  const [localHeightError, setLocalHeightError] = useState<string>("");
  const [localAgeError, setLocalAgeError] = useState<string>("");
  
  const handleHeightChange = (value: string) => {
    setHeight(value);
    const result = parseHeight(value);
    const error = result.valid ? "" : (result.error || "");
    setLocalHeightError(error);
    setHeightError?.(error);
  };

  const handleAgeChange = (value: string) => {
    setAge(value);
    const result = parseAge(value);
    const error = result.valid ? "" : (result.error || "");
    setLocalAgeError(error);
    setAgeError?.(error);
  };

  // Validate on mount if there's existing height
  useEffect(() => {
    if (height) {
      const result = parseHeight(height);
      const error = result.valid ? "" : (result.error || "");
      setLocalHeightError(error);
      setHeightError?.(error);
    }
  }, []);

  return (
    <div className="w-full max-w-sm animate-fade-in space-y-4 max-h-[60vh] overflow-y-auto px-1 pb-2 -mx-1">
      <StepHeader icon={icon} title={title} description={description} />

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="age" className="text-xs">Age</Label>
            <Input
              id="age"
              type="number"
              placeholder="25"
              value={age}
              onChange={(e) => handleAgeChange(e.target.value)}
              className={`bg-secondary/30 border-border/50 h-9 ${localAgeError ? "border-destructive" : ""}`}
            />
            {localAgeError && (
              <p className="text-[10px] text-destructive">{localAgeError}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sex" className="text-xs">Sex</Label>
            <Select value={sex} onValueChange={setSex}>
              <SelectTrigger className="bg-secondary/30 border-border/50 h-9">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
                <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="height" className="text-xs">Height</Label>
            <Input
              id="height"
              type="text"
              placeholder="5'10&quot; or 178cm"
              value={height}
              onChange={(e) => handleHeightChange(e.target.value)}
              className={`bg-secondary/30 border-border/50 h-9 ${localHeightError ? "border-destructive" : ""}`}
            />
            {localHeightError && (
              <p className="text-[10px] text-destructive">{localHeightError}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="weight" className="text-xs">Weight</Label>
            <Input
              id="weight"
              type="number"
              placeholder="180 lbs"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="bg-secondary/30 border-border/50 h-9"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="experience" className="text-xs">Experience Level</Label>
          <Select value={experience} onValueChange={setExperience}>
            <SelectTrigger className="bg-secondary/30 border-border/50 h-9">
              <SelectValue placeholder="Select your level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="beginner">Beginner (&lt;1 year)</SelectItem>
              <SelectItem value="intermediate">Intermediate (1-4 years)</SelectItem>
              <SelectItem value="advanced">Advanced (5+ years)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
